import { Box, Button, Typography, getContrastRatio } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import '@fontsource/rubik';
import CustomAlert from '../../events/components/CustomAlert';
import useCustomTheme from '../../../theme/useCustomTheme';
import { generateLighterContrastColor } from '../../../utils/commonServices';

/**
 * One-line confirmation before a project is hidden from (or shown back on) the dashboard.
 * Worth the extra click on hide, because the project is about to disappear from the
 * asker's own board.
 *
 * Props:
 *   open, projectName, hidden (the value being applied), isSubmitting, onConfirm, onClose
 */
const HideOnPharoahConfirmationPopup = ({ open, projectName, hidden, isSubmitting, onConfirm, onClose }) => {
  const [theme] = useCustomTheme();

  const cancelColor =
    theme.palette.primary.main === ('#00d744' || '#00D744')
      ? '#00d744'
      : getContrastRatio('#08231B', theme.palette.primary.main) > 4.5
      ? `${theme.palette.primary.main}`
      : generateLighterContrastColor(theme.palette.primary.main);

  return (
    <CustomAlert
      open={!!open}
      handleClose={isSubmitting ? undefined : onClose}
      width={{ xs: '92vw', sm: '400px', md: '420px', lg: '460px', xl: '620px' }}
      height={{ xs: '300px', sm: '300px', md: '320px', lg: '330px', xl: '420px' }}
    >
      <Box p={{ xs: 3, md: 5 }} className="flex col j-center" sx={{ height: '100%' }} data-testid="hide-pharoah-confirmation">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {hidden ? (
            <VisibilityOffOutlinedIcon sx={{ fontSize: { xs: '18px', lg: '20px', xl: '26px' }, color: '#5A6474' }} />
          ) : (
            <VisibilityOutlinedIcon sx={{ fontSize: { xs: '18px', lg: '20px', xl: '26px' }, color: '#2E7D5B' }} />
          )}
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontWeight: 600,
              fontSize: { xs: '13px', lg: '15px', xl: '20px' },
              color: 'black',
            }}
          >
            {hidden ? 'Hide from dashboard?' : 'Show on dashboard?'}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: { xs: '11px', md: '12px', lg: '13px', xl: '17px' },
            color: 'black',
          }}
        >
          {hidden ? (
            <>
              {'"'}
              <Typography component="span" sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 'inherit' }}>
                {projectName}
              </Typography>
              {'" will disappear from the board and the project list for everyone on the dashboard — including you.'}
            </>
          ) : (
            <>
              {'"'}
              <Typography component="span" sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 'inherit' }}>
                {projectName}
              </Typography>
              {'" will appear on the board and the project list for everyone on the dashboard again.'}
            </>
          )}
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Rubik',
            fontSize: { xs: '10px', md: '10.5px', lg: '11px', xl: '14px' },
            color: '#5A6474',
            mt: 1.5,
          }}
        >
          This only affects the dashboard. The client still sees this project in QueenBee exactly as before.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'end' }} gap={2} mt={{ xs: 2, md: 3 }}>
          <Button
            variant="outlined"
            color="button"
            disabled={!!isSubmitting}
            onClick={onClose}
            style={{ borderRadius: '5px', color: cancelColor, textTransform: 'none' }}
            sx={{
              fontSize: { xs: '9px', md: '9px', lg: '11px', xl: '16px' },
              fontWeight: 600,
              width: { xs: '76px', lg: '86px', xl: '129px' },
              paddingY: 0.4,
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            disabled={!!isSubmitting}
            loading={!!isSubmitting}
            loadingPosition="start"
            onClick={onConfirm}
            variant="contained"
            style={{ borderRadius: '5px', backgroundColor: '#08231B', color: theme.palette.primary.main, textTransform: 'none' }}
            sx={{
              fontSize: { xs: '9px', md: '9px', lg: '11px', xl: '16px' },
              fontWeight: 600,
              minWidth: '110px !important',
            }}
          >
            {hidden ? 'Hide' : 'Show'}
          </LoadingButton>
        </Box>
      </Box>
    </CustomAlert>
  );
};

export default HideOnPharoahConfirmationPopup;
